import { getCellTypes } from '@platejs/table';
import { ensureMinColWidths, redistributeColSizesAfterInsert } from '../../serializers/tableColSizes';

// Pure path-math for selecting an entire table row or column. Shared by the
// cell context menu (tableElements) and the row/column drag-handles
// (TableHandlesOverlay) so the grid-coordinate logic lives in one place; each
// caller keeps its own side effects (focus, scroll restoration). Returns null
// paths when the table/row is empty so callers can short-circuit before
// touching the editor.
// Stable unique id for a table cell (td/th). @platejs/table keys its merged-cell
// index matrix (computeCellIndices → `_cellIndices`) on `cell.id`; with no id
// every cell collapses to {col:0,row:0}, so merged cells become unselectable and
// the per-cell index cache is defeated (computeCellIndices re-runs O(cells) every
// render). NodeIdPlugin — which would stamp these — is only registered under
// enableBlockDnd (off everywhere), so a dedicated cell normalizer assigns them
// instead. Mirrors the ColumnItemPlugin `col-...` id scheme in usePlateEditorLogic.
export function generateCellId(): string {
    return `cell-${Math.random().toString(36).slice(2, 10)}`;
}

// Split-eligibility for the "Split cell" context-menu entry, computed from the
// cell node itself so no editor-wide selection subscription is needed (the
// library's useTableMergeState registered three per cell — see the disableMerge
// option in usePlateEditorLogic). @platejs/table's splitTableCell operates on
// colSpan/rowSpan directly and is not merge-gated, so it stays functional with
// merging disabled.
export function isCellSplitEligible(cellElement: any): boolean {
    return (cellElement?.colSpan ?? 1) > 1 || (cellElement?.rowSpan ?? 1) > 1;
}

export function calculateSelectionRange(
    axis: 'row' | 'column',
    tableNode: any,
    tablePath: number[],
    index: number,
): { start: number[] | null; end: number[] | null } {
    if (!tableNode?.children?.length) return { start: null, end: null };
    if (axis === 'row') {
        const row = tableNode.children[index];
        if (!row?.children?.length) return { start: null, end: null };
        return { start: [...tablePath, index, 0], end: [...tablePath, index, row.children.length - 1] };
    }
    return { start: [...tablePath, 0, index], end: [...tablePath, tableNode.children.length - 1, index] };
}

// A cell's structural child-index only equals its visual column when the
// table has no merges. A colSpan/rowSpan cell reserves extra grid slots that
// push every later sibling's child-index one or more columns short of its
// visual position, and a rowSpan cell's reservation carries into rows below
// it that never list that cell as a child at all. calculateRectRange needs
// the VISUAL rectangle (what the highlight shows), so when the table has any
// span it resolves through this occupancy grid instead of raw child-index
// math. `spans[row][childIndex]` is a cell's own visual column range;
// `grid[row][col]` is the structural (row, childIndex) that occupies a given
// visual cell, which may belong to an earlier row via rowSpan.
type CellSpan = { start: number; end: number };
type CellRef = { row: number; childIndex: number };

function buildVisualGrid(tableNode: any): { grid: CellRef[][]; spans: CellSpan[][] } {
    const rows: any[] = tableNode?.children ?? [];
    const grid: CellRef[][] = rows.map(() => []);
    const spans: CellSpan[][] = rows.map(() => []);
    for (let row = 0; row < rows.length; row++) {
        const cells: any[] = rows[row]?.children ?? [];
        let col = 0;
        for (let childIndex = 0; childIndex < cells.length; childIndex++) {
            while (grid[row][col]) col++;
            const cell = cells[childIndex];
            const colSpan = Math.max(1, cell?.colSpan ?? 1);
            const rowSpan = Math.max(1, cell?.rowSpan ?? 1);
            const start = col;
            const end = col + colSpan - 1;
            spans[row][childIndex] = { start, end };
            for (let r = row; r < Math.min(row + rowSpan, rows.length); r++) {
                for (let c = start; c <= end; c++) grid[r][c] = { row, childIndex };
            }
            col = end + 1;
        }
    }
    return { grid, spans };
}

// Exported for the F4 reorder gate (TableHandlesOverlay) — a table with any
// merge should suppress the drag-to-reorder affordance (see that file).
export function tableHasSpans(tableNode: any): boolean {
    return !!tableNode?.children?.some(
        (row: any) => row?.children?.some((cell: any) => (cell?.colSpan ?? 1) > 1 || (cell?.rowSpan ?? 1) > 1),
    );
}

// Pure path-math for Shift+Click range selection: given two arbitrary cell
// paths (anchor + clicked) in the SAME table, returns the top-left and
// bottom-right corners of the rectangle spanning both — i.e. Excel's
// "shift-click extends to a rectangle between the two cells" behavior.
// Cell paths are [...tablePath, rowIdx, colIdx] (shared assumption with
// moveCellSelection in cellSelection.ts). Returns null corners when the two
// paths don't share a table path prefix (shift-click across two different
// tables — not a valid rectangle, caller should ignore).
//
// `tableNode` is the actual table element (rows -> cells, with colSpan/
// rowSpan) so merged cells resolve through the visual grid above; pass
// null/undefined when the caller has no merges to account for (or none at
// all, e.g. plain-path unit tests) and the raw child-index math applies as-is.
export function calculateRectRange(
    tableNode: any,
    anchorCellPath: number[],
    targetCellPath: number[],
): { start: number[] | null; end: number[] | null } {
    if (anchorCellPath.length !== targetCellPath.length || anchorCellPath.length < 2) return { start: null, end: null };
    const tableLen = anchorCellPath.length - 2;
    const anchorTablePath = anchorCellPath.slice(0, tableLen);
    const targetTablePath = targetCellPath.slice(0, tableLen);
    if (anchorTablePath.join() !== targetTablePath.join()) return { start: null, end: null };

    const anchorRow = anchorCellPath[tableLen];
    const anchorChildIndex = anchorCellPath[tableLen + 1];
    const targetRow = targetCellPath[tableLen];
    const targetChildIndex = targetCellPath[tableLen + 1];

    if (!tableHasSpans(tableNode)) {
        const minRow = Math.min(anchorRow, targetRow);
        const maxRow = Math.max(anchorRow, targetRow);
        const minCol = Math.min(anchorChildIndex, targetChildIndex);
        const maxCol = Math.max(anchorChildIndex, targetChildIndex);
        return { start: [...anchorTablePath, minRow, minCol], end: [...anchorTablePath, maxRow, maxCol] };
    }

    const { grid, spans } = buildVisualGrid(tableNode);
    const anchorSpan = spans[anchorRow]?.[anchorChildIndex];
    const targetSpan = spans[targetRow]?.[targetChildIndex];
    if (!anchorSpan || !targetSpan) return { start: null, end: null };

    const minRow = Math.min(anchorRow, targetRow);
    const maxRow = Math.max(anchorRow, targetRow);
    const minVisualCol = Math.min(anchorSpan.start, targetSpan.start);
    const maxVisualCol = Math.max(anchorSpan.end, targetSpan.end);

    const startRef = grid[minRow]?.[minVisualCol];
    const endRef = grid[maxRow]?.[maxVisualCol];
    if (!startRef || !endRef) return { start: null, end: null };

    return {
        start: [...anchorTablePath, startRef.row, startRef.childIndex],
        end: [...anchorTablePath, endRef.row, endRef.childIndex],
    };
}

// Context-menu "Insert column left/right". insertTableColumn splices a 0 into
// a fully-sized table's colSizes, and a 0 column renders at zero width when the
// existing columns already fill the table (the invisible-inserted-column bug —
// full story on redistributeColSizesAfterInsert). After the library transform
// runs, locate the spliced 0 by diffing colSizes against the pre-insert array
// and replace the whole array with the redistributed widths. Diffing (rather
// than precomputing the index from the clicked cell) stays correct however the
// transform resolves the source cell from the selection. `cellElement` only
// anchors the table lookup.
//
// Every other colSizes shape falls through to the ensureMinColWidths guarantee:
// a healthy auto-width table ([300,0,0] with real leftover) keeps its spliced 0
// as before, but a doc saved with an already-crushed auto column — a pre-fix
// insert spliced a 0 into a fully-sized table — fails the fully-positive diff
// above and used to splice yet another invisible column; the guarantee repairs
// whatever the transform produced so no column ever renders below the minimum.
export function insertTableColumnSized(editor: any, cellElement: any, before?: boolean): void {
    try {
        const cellPath: number[] | undefined = editor?.api?.findPath?.(cellElement);
        const tablePath = cellPath && cellPath.length >= 2 ? cellPath.slice(0, -2) : undefined;
        const tableBefore: any = tablePath && editor?.api?.node?.(tablePath)?.[0];
        const prev: number[] | null = Array.isArray(tableBefore?.colSizes) ? [...tableBefore.colSizes] : null;

        editor?.tf?.insert?.tableColumn?.(before ? { before: true } : undefined);

        if (!prev || !tablePath) return;
        const tableAfter: any = editor?.api?.node?.(tablePath)?.[0];
        const next: unknown = tableAfter?.colSizes;
        if (!Array.isArray(next)) return;

        let sized: number[] = next as number[];
        const fullyPositive = prev.every((w: any) => typeof w === 'number' && Number.isFinite(w) && w > 0);
        if (fullyPositive && next.length === prev.length + 1) {
            const insertIndex = next.findIndex((w) => !w);
            const rest = insertIndex >= 0 ? next.filter((_, i) => i !== insertIndex) : null;
            if (rest && rest.every((w, i) => w === prev[i])) {
                sized = redistributeColSizesAfterInsert(prev, insertIndex);
            }
        }
        sized = ensureMinColWidths(sized);
        if (sized !== next) {
            editor.tf.setNodes({ colSizes: sized }, { at: tablePath });
        }
    } catch {
        /* defensive — table may have been mutated under us */
    }
}

// Context-menu "Delete row" / "Delete column" (RECUR-26973 bug fixes). Both
// wrap the library's own remove.tableRow()/tableColumn() transform so a span
// that used to cover the removed row/column keeps pointing at cells that
// still exist, instead of being left corrupt (the Cell renderer forwards
// element.rowSpan/colSpan straight onto the DOM td/th — see tableElements.tsx
// domRowSpan/domColSpan).

// Bug 1: a row deleted out from under an EARLIER row's rowSpan left that
// span pointing past the table's new (shorter) row count. The row index
// being removed is captured BEFORE the delete (paths for rows above it are
// unaffected by the removal), then every rowSpan that covered it is shrunk.
export function deleteTableRowWithSpanFix(editor: any): void {
    const tableEntry = editor?.api?.above?.({ match: { type: 'table' } });
    const rowEntry = tableEntry && !editor?.api?.isExpanded?.() ? editor?.api?.above?.({ match: { type: 'tr' } }) : undefined;
    const deletedRowIndex: number | undefined = rowEntry ? rowEntry[1][rowEntry[1].length - 1] : undefined;

    editor?.tf?.remove?.tableRow?.();

    if (tableEntry && deletedRowIndex != null) {
        shrinkRowSpansPastDeletedRow(editor, tableEntry[1], deletedRowIndex);
    }
}

function shrinkRowSpansPastDeletedRow(editor: any, tablePath: number[], deletedRowIndex: number): void {
    const tableNode: any = editor?.api?.node?.(tablePath)?.[0];
    const rows: any[] = tableNode?.children ?? [];
    for (let rowIndex = 0; rowIndex < deletedRowIndex && rowIndex < rows.length; rowIndex++) {
        (rows[rowIndex]?.children ?? []).forEach((cell: any, cellIndex: number) => {
            const rowSpan = cell?.rowSpan ?? 1;
            if (rowSpan <= 1 || rowIndex + rowSpan - 1 < deletedRowIndex) return;
            const newRowSpan = Math.max(1, rowSpan - 1);
            const props: any = { rowSpan: newRowSpan };
            if (cell?.attributes?.rowspan != null) props.attributes = { ...cell.attributes, rowspan: String(newRowSpan) };
            editor.tf.setNodes(props, { at: [...tablePath, rowIndex, cellIndex] });
        });
    }
}

// Bug 2: a right-clicked cell NOT part of a real multi-cell selection is left
// with an EXPANDED whole-cell range by selectCell/open() (tableElements.tsx).
// @platejs/table's remove.tableColumn routes any expanded selection into
// deleteColumnWhenExpanded, which compares tr node `.id`s that this app never
// assigns — both sides are `undefined`, so its "does the selection span the
// full table height" guard always passes, and it ends up removing only the
// cells the (single-cell) selection covers instead of the whole column,
// leaving a jagged table. Resolve the target column from the anchor cell —
// same as the collapsed-caret path — whenever the expanded selection covers
// exactly one cell, then let the library's own single-column path (which
// already deletes the whole column) run.
export function deleteTableColumnWithSpanFix(editor: any): void {
    const tableEntry = editor?.api?.above?.({ match: { type: 'table' } });
    const cellTypes = getCellTypes(editor);

    if (tableEntry && editor?.api?.isExpanded?.()) {
        const anchorCell = editor?.api?.above?.({ at: editor.selection.anchor, match: { type: cellTypes } });
        const focusCell = editor?.api?.above?.({ at: editor.selection.focus, match: { type: cellTypes } });
        if (anchorCell && focusCell && anchorCell[0] === focusCell[0]) {
            editor.tf.select(editor.api.start(anchorCell[1]));
        }
    }

    const cellEntry = !editor?.api?.isExpanded?.() ? editor?.api?.above?.({ match: { type: cellTypes } }) : undefined;
    const deletedColIndex: number | undefined = cellEntry ? cellEntry[1][cellEntry[1].length - 1] : undefined;

    editor?.tf?.remove?.tableColumn?.();

    if (tableEntry && deletedColIndex != null) {
        shrinkColSpansPastDeletedColumn(editor, tableEntry[1], deletedColIndex);
    }
}

function shrinkColSpansPastDeletedColumn(editor: any, tablePath: number[], deletedColIndex: number): void {
    const tableNode: any = editor?.api?.node?.(tablePath)?.[0];
    const rows: any[] = tableNode?.children ?? [];
    rows.forEach((row: any, rowIndex: number) => {
        (row?.children ?? []).forEach((cell: any, cellIndex: number) => {
            const colSpan = cell?.colSpan ?? 1;
            if (colSpan <= 1 || cellIndex >= deletedColIndex || cellIndex + colSpan - 1 < deletedColIndex) return;
            const newColSpan = Math.max(1, colSpan - 1);
            const props: any = { colSpan: newColSpan };
            if (cell?.attributes?.colspan != null) props.attributes = { ...cell.attributes, colspan: String(newColSpan) };
            editor.tf.setNodes(props, { at: [...tablePath, rowIndex, cellIndex] });
        });
    });
}
